import React, { useState } from "react";
import { Play } from "lucide-react";
import SectionHeading from "./SectionHeading";

/**
 * Lazy YouTube gallery — shows thumbnails until a tile is clicked, then
 * swaps in the autoplay iframe in-place. Avoids 3 iframes hitting YouTube
 * on page load.
 *
 * To swap in the real videos: replace the `id` field on each entry with the
 * 11-character YouTube video ID (the bit after `?v=` or `youtu.be/`).
 */
const VIDEOS = [
  {
    mp4: "https://customer-assets.emergentagent.com/job_guest-intelligence/artifacts/jwnoi9x3_boomerang.mp4",
    title: "Tech Brand Launch · San Francisco",
    subtitle: "847 leads · 340 influencers · 90s recap",
  },
  {
    id: "NRfb5wbNdsE",
    title: "Luxury Hotel · Singapore",
    subtitle: "Lobby installation · loyalty in motion",
  },
  {
    id: "BxBaMn1o8Ak",
    title: "Premium Restaurant · Miami",
    subtitle: "Entrance moment · 18% → 42% repeat rate",
  },
  {
    id: "3qKInxRlAEE",
    title: "Reel · 04",
    subtitle: "Live activation · captured by Stori",
  },
  {
    id: "X_rO6i-xVgc",
    title: "Reel · 05",
    subtitle: "Live activation · captured by Stori",
  },
  {
    id: "hdhQ91jEAIY",
    title: "Reel · 06",
    subtitle: "Live activation · captured by Stori",
  },
];

export default function VideoGallery() {
  const [playing, setPlaying] = useState(() => new Set());

  const start = (i) => setPlaying((prev) => new Set(prev).add(i));

  return (
    <section
      data-testid="video-gallery"
      className="relative py-24 lg:py-32 border-b border-stori-border bg-stori-bg"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="LIVE EVENTS · BY STORI"
          title="Where the moment becomes the marketing."
          lead="Live events powered by Stori — captured in real time."
        />

        <div className="mt-14 lg:mt-16 grid md:grid-cols-3 gap-4 lg:gap-5">
          {VIDEOS.map((v, i) => {
            const isPlaying = playing.has(i);
            const thumb = v.id ? `https://i.ytimg.com/vi/${v.id}/hqdefault.jpg` : null;
            return (
              <article
                key={i}
                data-testid={`video-tile-${i}`}
                className="group rounded-2xl overflow-hidden border border-stori-border bg-stori-surface1 hover:border-stori-cyan/40 transition-colors"
              >
                <div className="relative aspect-video bg-black">
                  {v.mp4 ? (
                    <>
                      <video
                        src={v.mp4}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.22em] text-white bg-stori-bg/70 backdrop-blur px-2 py-1 rounded">
                        Reel · 0{i + 1}
                      </span>
                    </>
                  ) : isPlaying ? (
                    <iframe
                      title={v.title}
                      src={`https://www.youtube.com/embed/${v.id}?autoplay=1&rel=0&modestbranding=1`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                      style={{ border: 0 }}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => start(i)}
                      data-testid={`video-play-${i}`}
                      aria-label={`Play ${v.title}`}
                      className="absolute inset-0 w-full h-full overflow-hidden focus:outline-none"
                    >
                      <img
                        src={thumb}
                        alt={v.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-stori-bg/80 via-stori-bg/20 to-transparent" />
                      {/* Play button */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="relative flex items-center justify-center w-16 h-16 rounded-full bg-stori-cyan text-black shadow-[0_0_40px_rgba(0,229,255,0.4)] transition-transform group-hover:scale-110">
                          <Play className="w-6 h-6 ml-0.5" fill="currentColor" />
                          <span className="absolute inset-0 rounded-full pulse-ring" />
                        </span>
                      </div>
                      {/* Reel index */}
                      <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.22em] text-white bg-stori-bg/70 backdrop-blur px-2 py-1 rounded">
                        Reel · 0{i + 1}
                      </span>
                    </button>
                  )}
                </div>
                <div className="p-5 lg:p-6">
                  <h3 className="font-heading text-lg font-semibold text-white tracking-tight">
                    {v.title}
                  </h3>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.18em] text-stori-muted">
                    {v.subtitle}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
